import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, message, Select, Space, Table, Tag } from 'antd';
import moment from 'moment';
import { useMemo, useState } from 'react';

import { axiosClient } from '@/api/axiosClient';

// V1.4 D2.1: BE now LEFT JOINs worker + plan so the FE table can render the
// real name/phone/plan_name instead of a UUID slice. The 4 enriched columns
// fall through as empty strings if the join misses (defensive).
interface ApplicationRow {
  id: string;
  worker_id: string;
  plan_id: string;
  status: string;
  note?: string;
  submitted_at: string;
  assigned_by?: string;
  reject_reason?: string;
  worker_name?: string;
  worker_phone?: string;
  plan_name?: string;
  plan_code?: string;
}

const STATUS_COLOR: Record<string, string> = {
  ASSIGNED: 'green',
  AUTO_REJECTED: 'red',
  PENDING: 'blue',
  REJECTED: 'red',
};

// T-TT-03 V1.4 — NV TD review đơn ứng tuyển. Assign 1 đơn tự auto-reject
// các đơn PENDING khác của cùng NLĐ (V1.4 B2 multi-app rule).
interface PlanRow {
  id: string;
  name: string;
  code?: string;
}

const ApplicationPage = () => {
  const [status, setStatus] = useState('PENDING');
  const [planId, setPlanId] = useState<string>('');

  // V1.4 D4 — campaign filter (T-TT-03 DoD #2). Plan list loaded once;
  // pageSize=200 fits the typical demo dataset.
  const { data: plans } = useQuery(
    ['plans-for-app-filter'],
    () =>
      axiosClient.get<{ rows: PlanRow[] }>('/recruitment-plan/filter-plans?page_index=1&page_size=200'),
    { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000 }
  );
  const planOptions = useMemo(
    () =>
      ((plans as unknown as { data?: { rows?: PlanRow[] } })?.data?.rows ?? [])
        .map((p) => ({
          label: p.name + (p.code ? ` [${p.code}]` : ''),
          value: p.id,
        })),
    [plans]
  );

  const { data, isLoading, refetch } = useQuery(
    ['applications', status, planId],
    () => {
      const qs = new URLSearchParams({ status });
      if (planId) qs.set('plan_id', planId);

      return axiosClient.get<{ rows: ApplicationRow[] }>(
        `/application/filter?${qs.toString()}`
      );
    },
    { refetchOnWindowFocus: false }
  );

  const assignMutation = useMutation(
    (id: string) => axiosClient.patch('/application/assign', { id }),
    {
      onSuccess: () => {
        message.success('Đã nhận NLĐ vào chiến dịch. Các đơn khác auto-reject.');
        refetch();
      },
    }
  );

  const columns = [
    {
      dataIndex: 'submitted_at',
      key: 'sub',
      render: (v: string) => moment(v).format('DD/MM HH:mm'),
      title: 'Ngày nộp',
      width: 130,
    },
    {
      key: 'w',
      render: (_: unknown, row: ApplicationRow) => (
        <span>
          {row.worker_name || row.worker_id.slice(0, 8) + '…'}
          {row.worker_phone && (
            <span style={{ color: '#999', fontSize: 12, marginLeft: 6 }}>
              {row.worker_phone}
            </span>
          )}
        </span>
      ),
      title: 'NLĐ',
    },
    {
      key: 'p',
      render: (_: unknown, row: ApplicationRow) => (
        <span>
          {row.plan_name || row.plan_id.slice(0, 8) + '…'}
          {row.plan_code && (
            <span style={{ color: '#999', fontSize: 12, marginLeft: 6 }}>
              [{row.plan_code}]
            </span>
          )}
        </span>
      ),
      title: 'Chiến dịch',
    },
    {
      dataIndex: 'status',
      key: 's',
      render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
      title: 'Trạng thái',
    },
    { dataIndex: 'note', key: 'n', title: 'Ghi chú' },
    { dataIndex: 'reject_reason', key: 'rr', title: 'Lý do reject' },
    {
      key: 'a',
      render: (_: unknown, row: ApplicationRow) =>
        row.status === 'PENDING' && (
          <Button
            loading={assignMutation.isLoading}
            onClick={() => assignMutation.mutate(row.id)}
            size="small"
            type="primary"
          >
            Assign
          </Button>
        ),
      title: 'Hành động',
    },
  ];

  return (
    <Card
      extra={
        <Space>
          <Select
            allowClear
            onChange={(v) => setPlanId(v || '')}
            optionFilterProp="label"
            options={planOptions}
            placeholder="Lọc chiến dịch"
            showSearch
            style={{ width: 280 }}
            value={planId || undefined}
          />
          <Select
            onChange={setStatus}
            options={['PENDING', 'ASSIGNED', 'REJECTED', 'AUTO_REJECTED'].map((s) => ({
              label: s,
              value: s,
            }))}
            style={{ width: 180 }}
            value={status}
          />
        </Space>
      }
      title="Đơn ứng tuyển từ App (T-TT-03 V1.4 Option X)"
    >
      <Table<ApplicationRow>
        columns={columns}
        dataSource={
          (data as unknown as { data?: { rows: ApplicationRow[] } })?.data?.rows || []
        }
        loading={isLoading}
        rowKey="id"
        size="middle"
      />
    </Card>
  );
};

export default ApplicationPage;
